import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs, Spin, Modal } from 'antd';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';

import OTALogo from 'components/OTALogo/OTALogo';

import { getRoomTypeByProperty } from 'utils/apis/integration';
import { isAirbnbRoomTypeFeatureEnabled } from 'utils/featureFlags';

import AgodaForm from './components/Agoda/AgodaForm';
import AirbnbHotelForm from './components/Airbnb/AirbnbHotelForm';
import BookingcomForm from './components/Bookingcom/BookingcomForm';
import CtripForm from './components/Ctrip/CtripForm';
import CtripCMForm from './components/CtripCM/CtripCMForm';
import ExpediaForm from './components/Expedia/ExpediaForm';
import TravelokaForm from './components/Traveloka/TravelokaForm';
import TiketcomForm from './components/Tiketcom/TiketcomForm';

import { REACT_APP_IS_SHOW_TIKETCOM, REACT_APP_IS_SHOW_TRAVELOKA } from 'config/env';

const TabPane = Tabs.TabPane;

const useSelectedTabDetails = selectedIntegrationSource => {
  const [selectedTab, setSelectedTab] = useState(selectedIntegrationSource);

  const handleOnChangeTab = integrationSourceCode => {
    setSelectedTab(integrationSourceCode);
  };

  return { selectedTab, handleOnChangeTab };
};

const useFetchAndConstructPropertyWithRoomTypes = selectedProperty => {
  const [selectedPropertyWithRoomTypes, setselectedPropertyWithRoomTypes] = useState({});
  const [isLoadingProperty, setIsLoadingProperty] = useState(true);

  const fetchAndConstructPropertyWithRoomTypes = useCallback(async () => {
    try {
      setIsLoadingProperty(true);
      const roomTypes = await getRoomTypeByProperty(selectedProperty._id).then(res => res.data);
      setselectedPropertyWithRoomTypes({ ...selectedProperty, roomTypes });
    } catch (error) {
      console.error('Failed to load room types:', error);
      setselectedPropertyWithRoomTypes(selectedProperty); // Fallback to property without room types
    } finally {
      setIsLoadingProperty(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchAndConstructPropertyWithRoomTypes();
  }, [fetchAndConstructPropertyWithRoomTypes]);

  return { selectedPropertyWithRoomTypes, isLoadingProperty };
};

const OTAHotelForm = ({
  integrationSourcesConstant,
  hotelIntegrations,
  selectedProperty,
  selectedIntegrationSourceCode,
  propertyHostId,
  onAfterSave,
  onClose
}) => {
  const { selectedTab, handleOnChangeTab } = useSelectedTabDetails(selectedIntegrationSourceCode || integrationSourcesConstant.AGODA.code);
  const { selectedPropertyWithRoomTypes, isLoadingProperty } = useFetchAndConstructPropertyWithRoomTypes(selectedProperty);

  const selectedHotelIntegration = useMemo(() => hotelIntegrations.find(hotelIntegration => hotelIntegration.source === selectedTab), [
    hotelIntegrations,
    selectedTab
  ]);

  const [isCTripLoading, setIsCTripLoading] = useState(true);
  return (
    <Modal
      visible={true}
      title={`${intl
        .get('hostConnect.integration.headerLabels.otaIntegrationProperty')
        .d('OTA Integration for Property')} ${selectedPropertyWithRoomTypes.name || ''}`}
      width="60%"
      destroyOnClose={true}
      footer={null}
      maskClosable={false}
      centered
      onCancel={onClose}
      bodyStyle={{ height: '75vh' }}
    >
      {isLoadingProperty ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs activeKey={selectedTab} onChange={handleOnChangeTab}>
          <TabPane
            tab={<OTALogo size="small" otaCode={integrationSourcesConstant.AGODA.code} text={integrationSourcesConstant.AGODA.label} />}
            key={integrationSourcesConstant.AGODA.code}
          >
            <AgodaForm property={selectedPropertyWithRoomTypes} onClose={onClose} data={selectedHotelIntegration} />
          </TabPane>
          <TabPane
            tab={<OTALogo size="small" otaCode={integrationSourcesConstant.EXPEDIA.code} text={integrationSourcesConstant.EXPEDIA.label} />}
            key={integrationSourcesConstant.EXPEDIA.code}
          >
            <ExpediaForm property={selectedPropertyWithRoomTypes} onClose={onClose} data={selectedHotelIntegration} />
          </TabPane>
          <TabPane
            tab={<OTALogo size="small" otaCode={integrationSourcesConstant.BOOKINGCOM.code} text={integrationSourcesConstant.BOOKINGCOM.label} />}
            key={integrationSourcesConstant.BOOKINGCOM.code}
          >
            <BookingcomForm property={selectedPropertyWithRoomTypes} onClose={onClose} onAfterSave={onAfterSave} data={selectedHotelIntegration} />
          </TabPane>
          {/* <TabPane
          tab={<OTALogo size="small" otaCode={integrationSourcesConstant.CTRIP.code} text={integrationSourcesConstant.CTRIP.label} />}
          key={integrationSourcesConstant.CTRIP.code}
        >
          <Spin spinning={isCTripLoading}>
            <CtripForm
              property={selectedPropertyWithRoomTypes}
              onClose={onClose}
              data={selectedHotelIntegration}
              stopLoading={() => setIsCTripLoading(false)}
            />
          </Spin>
        </TabPane> */}
          <TabPane
            tab={<OTALogo size="small" otaCode={integrationSourcesConstant.CTRIPCM.code} text={integrationSourcesConstant.CTRIPCM.label} />}
            key={integrationSourcesConstant.CTRIPCM.code}
          >
            <CtripCMForm property={selectedPropertyWithRoomTypes} onClose={onClose} onAfterSave={onAfterSave} data={selectedHotelIntegration} />
          </TabPane>
          {REACT_APP_IS_SHOW_TRAVELOKA && (
            <TabPane
              tab={<OTALogo size="small" otaCode={integrationSourcesConstant.TRAVELOKA.code} text={integrationSourcesConstant.TRAVELOKA.label} />}
              key={integrationSourcesConstant.TRAVELOKA.code}
            >
              <TravelokaForm
                property={selectedPropertyWithRoomTypes}
                onClose={onClose}
                onAfterSave={onAfterSave}
                selectedHotelIntegration={selectedHotelIntegration}
              />
            </TabPane>
          )}

          {REACT_APP_IS_SHOW_TIKETCOM && (
            <TabPane
              tab={<OTALogo size="small" otaCode={integrationSourcesConstant.TIKETCOM.code} text={integrationSourcesConstant.TIKETCOM.label} />}
              key={integrationSourcesConstant.TIKETCOM.code}
            >
              <TiketcomForm
                property={selectedPropertyWithRoomTypes}
                onClose={onClose}
                onAfterSave={onAfterSave}
                selectedHotelIntegration={selectedHotelIntegration}
              />
            </TabPane>
          )}
          {isAirbnbRoomTypeFeatureEnabled(propertyHostId) && (
            <TabPane
              tab={<OTALogo size="small" otaCode={integrationSourcesConstant.AIRBNB.code} text={integrationSourcesConstant.AIRBNB.label} />}
              key={integrationSourcesConstant.AIRBNB.code}
            >
              <AirbnbHotelForm
                property={selectedPropertyWithRoomTypes}
                onClose={onClose}
                onAfterSave={onAfterSave}
                data={selectedHotelIntegration}
                propertyHostId={propertyHostId}
              />
            </TabPane>
          )}
        </Tabs>
      )}
    </Modal>
  );
};

OTAHotelForm.propTypes = {
  integrationSourcesConstant: PropTypes.object.isRequired,
  hotelIntegrations: PropTypes.array,
  selectedProperty: PropTypes.object.isRequired,
  selectedIntegrationSourceCode: PropTypes.string,
  propertyHostId: PropTypes.string,
  onAfterSave: PropTypes.func,
  onClose: PropTypes.func
};

OTAHotelForm.defaultProps = {
  hotelIntegrations: [],
  selectedIntegrationSourceCode: undefined,
  onAfterSave: () => {},
  onClose: () => {}
};

export default OTAHotelForm;
